import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Container, Flex, Stack, StackDivider } from '@chakra-ui/react'
import html2canvas from 'html2canvas'

import { FormColorTheme, FormDto } from '~shared/types/form'

import BadgeFrame from '~assets/imgs/badge_frame.png'
import { useIsSMMobile } from '~hooks/useIsMobile'

import {
  SubmissionData,
  usePublicFormContext,
} from '~features/public-form/PublicFormContext'
import {
  setLangSelector,
  usePublicLanguageStore,
} from '~features/public-form/usePublicLanguageStore'

import { EndPageBlock } from './components/EndPageBlock'
import { FeedbackBlock, FeedbackFormInput } from './components/FeedbackBlock'
import { ThankYouSvgr } from './components/ThankYouSvgr'
import { nameSelector, useBadgeStore } from './useBadgeStore'

export interface FormEndPageProps {
  formTitle: FormDto['title']
  endPage: FormDto['endPage']
  submissionData: SubmissionData
  handleSubmitFeedback: (inputs: FeedbackFormInput) => void
  isFeedbackSubmitted: boolean
  colorTheme: FormColorTheme
}

const BadgeWithName = () => {
  // const { form } = usePublicFormContext()
  // TODO FormKH: track name of the badge, will delete later
  const { name } = useBadgeStore(
    useCallback((state) => ({ name: nameSelector(state) }), []),
  )
  const isMobile = useIsSMMobile()

  const badgeRef = useRef<HTMLDivElement>(null)

  const [isImageDownliading, setIsImageDownliading] = useState<boolean>(false)
  const htmlToImageConvert = useCallback(() => {
    if (badgeRef.current === null) {
      return
    }

    setIsImageDownliading(true)
    html2canvas(badgeRef.current, {
      backgroundColor: null,
      onclone: (doc, elem) => {
        if (elem.lastChild && elem.lastChild.style) {
          elem.lastChild.style.top = isMobile ? '215px' : '338px'
        }
      },
    }).then((canvas) => {
      const data = canvas.toDataURL('image/png')
      const link = document.createElement('a')
      link.href = data
      link.download = 'badge.png'

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setIsImageDownliading(false)
    })
  }, [badgeRef, isMobile])

  const imageStyle = isMobile
    ? { maxWidth: 'inherit', height: '455px', width: '315px' }
    : { maxWidth: 'inherit', height: '712px', width: '493px' }

  return (
    <div>
      <div
        ref={badgeRef}
        style={{
          fontFamily: 'Arial, Helvetica, sans-serif',
          borderCollapse: 'collapse',
          position: 'relative',
        }}
      >
        <img src={BadgeFrame} alt="badge" style={imageStyle} />
        <div
          id="name-box"
          style={{
            position: 'absolute',
            top: isMobile ? '230px' : '358px',
            left: isMobile ? '26px' : '41px',
            height: isMobile ? '110px' : '176px',
            width: isMobile ? '263px' : '412px',
            fontSize: isMobile ? '26px' : '38px',
            fontWeight: 'bolder',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '15px',
            wordBreak: 'break-all',
          }}
        >
          {name}
        </div>
      </div>
      {!isImageDownliading && (
        <button
          style={{ paddingTop: '15px', color: '#1e7aff' }}
          onClick={htmlToImageConvert}
          disabled={isImageDownliading}
        >
          ទាញយក / Download
        </button>
      )}
    </div>
  )
}

export const FormEndPage = ({
  handleSubmitFeedback,
  isFeedbackSubmitted,
  colorTheme,
  ...endPageProps
}: FormEndPageProps): JSX.Element => {
  const { form } = usePublicFormContext()
  const { setLang } = usePublicLanguageStore(
    useCallback(
      (state) => ({
        setLang: setLangSelector(state),
      }),
      [],
    ),
  )
  useEffect(() => {
    if (form) {
      setLang(form.lang)
    }
  }, [form?.lang, setLang])

  if (form?._id === '65b721b69a3d980012f64e1d') {
    return (
      <Container w="42.5rem" maxW="100%" p={0}>
        <Flex flexDir="column" align="center">
          <Stack
            spacing={{ base: '1.5rem', md: '3rem' }}
            py={{ base: '1.5rem', md: '3rem' }}
            px={{ base: '1.5rem', md: '4rem' }}
            bg="white"
            w="100%"
            divider={<StackDivider />}
          >
            <Flex justifyContent="center" textAlign="center">
              <Box>
                <BadgeWithName />
              </Box>
            </Flex>

            <EndPageBlock
              focusOnMount
              {...endPageProps}
              colorTheme={colorTheme}
            />
            {isFeedbackSubmitted ? null : (
              <FeedbackBlock
                colorTheme={colorTheme}
                onSubmit={handleSubmitFeedback}
              />
            )}
          </Stack>
        </Flex>
      </Container>
    )
  }

  return (
    <Container w="42.5rem" maxW="100%" p={0}>
      <Flex flexDir="column" align="center">
        <ThankYouSvgr maxW="100%" />
        <Stack
          spacing={{ base: '1.5rem', md: '3rem' }}
          py={{ base: '1.5rem', md: '3rem' }}
          px={{ base: '1.5rem', md: '4rem' }}
          bg="white"
          w="100%"
          divider={<StackDivider />}
        >
          <EndPageBlock
            focusOnMount
            {...endPageProps}
            colorTheme={colorTheme}
          />
          {isFeedbackSubmitted ? null : (
            <FeedbackBlock
              colorTheme={colorTheme}
              onSubmit={handleSubmitFeedback}
            />
          )}
        </Stack>
      </Flex>
    </Container>
  )
}
